import {useCallback, useEffect, useRef, useState} from 'react';
import {normalizePublicSilverQuote} from './publicSilverQuote';

export const PUBLIC_QUOTE_ENDPOINT = '/api/public/silver-quote';
export const PUBLIC_QUOTE_REFRESH_MS = 60000;
export const PUBLIC_QUOTE_TIMEOUT_MS = 10000;
const RESUME_THROTTLE_MS = 15000;
const INITIAL_STATE = {quote: null, loading: true, connection: 'loading', error: null};

export default function usePublicSilverQuote() {
  const [state, setState] = useState(INITIAL_STATE);
  const refreshRef = useRef(null);
  const refresh = useCallback(() => refreshRef.current?.() || Promise.resolve(null), []);

  useEffect(() => {
    let disposed = false;
    let activeRequest = null;
    let lastAttemptAt = -Infinity;

    const requestQuote = () => {
      if (disposed) return Promise.resolve(null);
      if (activeRequest) return activeRequest.promise;
      const request = {controller: new AbortController(), timer: null, promise: null};
      activeRequest = request;
      lastAttemptAt = Date.now();
      setState(current => ({...current, loading: true}));

      request.promise = (async () => {
        try {
          const timeout = new Promise((_, reject) => {
            request.timer = setTimeout(() => {
              const error = new Error('timeout');
              error.code = 'timeout';
              reject(error);
              request.controller.abort();
            }, PUBLIC_QUOTE_TIMEOUT_MS);
          });
          const response = await Promise.race([
            (async () => {
              const result = await fetch(PUBLIC_QUOTE_ENDPOINT, {
                method: 'GET', cache: 'no-store', credentials: 'omit',
                headers: {Accept: 'application/json'}, signal: request.controller.signal,
              });
              if (!result.ok) throw new Error('connection');
              return result.json();
            })(),
            timeout,
          ]);
          const quote = normalizePublicSilverQuote(response);
          if (!quote) throw new Error('invalid-response');
          if (disposed || activeRequest !== request) return null;
          setState({quote, loading: false, connection: quote.connection, error: quote.connection === 'retained' ? 'connection' : null});
          return quote;
        } catch (error) {
          if (disposed || activeRequest !== request) return null;
          setState(current => ({
            quote: current.quote ? {...current.quote, connection: 'retained'} : null,
            loading: false,
            connection: current.quote ? 'retained' : 'unavailable',
            error: error?.code === 'timeout' ? 'timeout' : 'connection',
          }));
          return null;
        } finally {
          clearTimeout(request.timer);
          if (activeRequest === request) activeRequest = null;
        }
      })();
      return request.promise;
    };

    const refreshOnResume = () => {
      if (document.visibilityState !== 'hidden' && Date.now() - lastAttemptAt >= RESUME_THROTTLE_MS) requestQuote();
    };
    refreshRef.current = requestQuote;
    requestQuote();
    const pollTimer = setInterval(() => {
      if (document.visibilityState !== 'hidden') requestQuote();
    }, PUBLIC_QUOTE_REFRESH_MS);
    document.addEventListener('visibilitychange', refreshOnResume);
    window.addEventListener('focus', refreshOnResume);

    return () => {
      disposed = true;
      refreshRef.current = null;
      clearInterval(pollTimer);
      document.removeEventListener('visibilitychange', refreshOnResume);
      window.removeEventListener('focus', refreshOnResume);
      if (activeRequest) {
        clearTimeout(activeRequest.timer);
        activeRequest.controller.abort();
      }
    };
  }, []);

  return {...state, refresh};
}
